import type { CaseDocumentMeta, MetricsSummary, ServerEvent, TurnTimeline, ArbiterEvent } from '@voicer/shared';
import { ws } from './ws/client';
import { useStore } from './state/store';
import { controller } from './conversation/controller';

async function loadCaseDocuments(caseId: string | null): Promise<void> {
  if (!caseId) {
    useStore.getState().setCaseDocuments([]);
    useStore.getState().setSelectedDocumentId(null);
    return;
  }
  try {
    const docs = await ws.rpc<CaseDocumentMeta[]>('case.documents', { caseId });
    useStore.getState().setCaseDocuments(docs);
  } catch {
    /* not connected or cases unavailable */
  }
}

let summaryTimer: number | null = null;
function refreshSummary() {
  if (summaryTimer) return;
  summaryTimer = window.setTimeout(async () => {
    summaryTimer = null;
    try {
      useStore.getState().setSummary(await ws.rpc<MetricsSummary>('metrics.summary'));
    } catch {
      /* not connected */
    }
  }, 300);
}

let bootstrapped = false;

export function bootstrap() {
  if (bootstrapped) {
    ws.connect();
    return;
  }
  bootstrapped = true;
  const st = () => useStore.getState();
  ws.onConnectionChange = (connected) => {
    st().setConnected(connected);
    if (!connected) controller.resetTurnState('disconnect');
  };
  ws.on((ev: ServerEvent) => {
    const s = st();
    switch (ev.type) {
      case 'hello':
        s.setSession(ev.data.session);
        s.setSettings(ev.data.settings);
        s.setStatus(ev.data.status);
        s.setSessions(ev.data.sessions);
        s.setProfiles(ev.data.profiles ?? []);
        s.setCases(ev.data.cases ?? []);
        s.setActiveCaseId(ev.data.activeCaseId ?? null);
        void loadCaseDocuments(ev.data.activeCaseId ?? null);
        void controller.applySettings(ev.data.settings);
        if (controller.hasActiveTurn()) controller.resetTurnState('reconnect');
        void ws.rpc<TurnTimeline[]>('metrics.turns').then((t) => s.setTurns(t)).catch(() => {});
        void ws.rpc<ArbiterEvent[]>('arbiter.list').then((e) => s.setArbiterEvents(e)).catch(() => {});
        refreshSummary();
        break;
      case 'status':
        s.setStatus(ev.data);
        break;
      case 'settings':
        s.setSettings(ev.data);
        void controller.applySettings(ev.data);
        break;
      case 'profiles':
        s.setProfiles(ev.data);
        break;
      case 'session':
        s.setSession(ev.data);
        void ws.rpc<TurnTimeline[]>('metrics.turns').then((t) => s.setTurns(t)).catch(() => {});
        void ws.rpc<ArbiterEvent[]>('arbiter.list').then((e) => s.setArbiterEvents(e)).catch(() => {});
        refreshSummary();
        break;
      case 'sessions':
        s.setSessions(ev.data);
        break;
      case 'cases':
        s.setCases(ev.data.cases);
        s.setActiveCaseId(ev.data.activeCaseId);
        void loadCaseDocuments(ev.data.activeCaseId);
        break;
      case 'message.upsert':
        s.upsertMessage(ev.data);
        break;
      case 'message.delete':
        s.deleteMessage(ev.data.id);
        break;
      case 'context.compiled':
        s.setCompiled(ev.data);
        break;
      case 'metrics.turn':
        s.addTurn(ev.data);
        refreshSummary();
        break;
      case 'metrics.summary':
        s.setSummary(ev.data);
        break;
      case 'arbiter.event':
        s.upsertArbiterEvent(ev.data);
        refreshSummary();
        break;
      case 'scheduler':
        s.setScheduler(ev.data);
        break;
      case 'background':
        s.addBackground(ev.data);
        break;
      case 'log':
        s.log(ev.data.level, ev.data.msg);
        break;
      default:
        break;
    }
  });
  ws.connect();
}
